import React, { useState } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  useToast,
  FormErrorMessage,
  VStack,
} from '@chakra-ui/react'
import apiService from '@/services/api'
import { useAppTranslation } from '@/contexts/LanguageContext'

interface InviteUserModalProps {
  isOpen: boolean
  onClose: () => void
  organizationId: number
  onSuccess: () => void
}

export default function InviteUserModal({
  isOpen,
  onClose,
  organizationId,
  onSuccess,
}: InviteUserModalProps) {
  const toast = useToast()
  const { t } = useAppTranslation('admin')
  const [formData, setFormData] = useState({
    email: '',
    role: 'viewer' as 'admin' | 'editor' | 'viewer',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)

  const handleInputChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value })
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' })
    }
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.email.trim()) {
      newErrors.email = t('inviteUser.validation.emailRequired')
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('inviteUser.validation.invalidEmail')
    }

    if (!formData.role) {
      newErrors.role = t('inviteUser.validation.roleRequired')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    try {
      setLoading(true)
      await apiService.inviteUserToOrganization(organizationId, {
        email: formData.email,
        role: formData.role,
      })
      onSuccess()
      handleClose()
    } catch (error: any) {
      toast({
        title: t('inviteUser.toast.error'),
        description: error.response?.data?.message || t('inviteUser.toast.errorDescription'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setFormData({ email: '', role: 'viewer' })
    setErrors({})
    setLoading(false)
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('inviteUser.title')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isRequired isInvalid={!!errors.email}>
              <FormLabel>{t('inviteUser.emailAddress')}</FormLabel>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder={t('inviteUser.emailPlaceholder')}
              />
              <FormErrorMessage>{errors.email}</FormErrorMessage>
            </FormControl>

            <FormControl isRequired isInvalid={!!errors.role}>
              <FormLabel>{t('inviteUser.role')}</FormLabel>
              <Select
                value={formData.role}
                onChange={(e) => handleInputChange('role', e.target.value)}
              >
                <option value="viewer">{t('inviteUser.roleViewer')}</option>
                <option value="editor">{t('inviteUser.roleEditor')}</option>
                <option value="admin">{t('inviteUser.roleAdmin')}</option>
              </Select>
              <FormErrorMessage>{errors.role}</FormErrorMessage>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            {t('inviteUser.cancel')}
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={loading}
            loadingText={t('inviteUser.sending')}
          >
            {t('inviteUser.send')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
